import { useEffect } from "react";
import type { UseFormSetValue, UseFormWatch } from "react-hook-form";
import { YesNoChoice } from "../../../../types/api.types";
import type { WizardFormModeProps } from "../../../../types/wizard.types";
import type {
  Person,
  Site,
  SiteCategoriesSummaryResponse
} from "../../../../utils/backend-types";

export interface UsePersonFormVisibilityArgs {
  modeProps: WizardFormModeProps<Person>;
  sites: Array<Site>;
  siteCategories?: SiteCategoriesSummaryResponse;
  watch: UseFormWatch<Person>;
  setValue: UseFormSetValue<Person>;
}

export interface GeneralFieldsVisibility {
  showIsSolarContractorField: boolean | undefined;
  showShouldRegulatoryDutiesBeCheckedField: boolean;
  showParticipationsInMieterstromModelField: boolean | undefined;
  showIsParagraph_6EegActiveField: boolean;
  showIsErloesmonitoringActiveField: boolean;
  showIsRedispatchImportActiveField: boolean;
  showAddressExtraField: boolean | undefined;
  showContactField: boolean;
  showTaxFormsApplicantLegalStructureField: boolean;
  showMastrNummerMarktakteurField: boolean;
  showGeneralPartnerFields: boolean;
}

export function usePersonFormVisibility({
  modeProps,
  sites,
  siteCategories,
  watch,
  setValue
}: UsePersonFormVisibilityArgs) {
  const isEditMode = modeProps.mode === "edit";
  const isInMieterstromProject = isEditMode
    ? modeProps.wizardObject?.isInMieterstromProject
    : false;
  const isSolarContractor = isEditMode
    ? modeProps.wizardObject?.isSolarContractor
    : false;

  // General tab visibility
  const isErloesmonitoringActive = watch("isErloesmonitoringActive");

  const generalFieldsVisibility: GeneralFieldsVisibility = {
    showIsSolarContractorField: isInMieterstromProject,
    showShouldRegulatoryDutiesBeCheckedField: isEditMode,
    showParticipationsInMieterstromModelField:
      isInMieterstromProject && isEditMode,
    showIsParagraph_6EegActiveField: isEditMode && !isInMieterstromProject,
    showIsErloesmonitoringActiveField: isEditMode && !isInMieterstromProject,
    showIsRedispatchImportActiveField:
      isEditMode && !isInMieterstromProject && !!isErloesmonitoringActive,
    showAddressExtraField: isInMieterstromProject && !isSolarContractor,
    showContactField: isEditMode && watch("contact") !== null,
    showTaxFormsApplicantLegalStructureField:
      isEditMode && (!isInMieterstromProject || !!isSolarContractor),
    showMastrNummerMarktakteurField:
      isEditMode && (!isInMieterstromProject || !!isSolarContractor),
    showGeneralPartnerFields: false // computed below
  };

  // General partner fields (complex dependency chain)
  const applicantLegalStructureValue = watch("taxFormsApplicantLegalStructure");
  const isLegalStructureWithPhg =
    applicantLegalStructureValue === "kg" ||
    applicantLegalStructureValue === "ohg" ||
    applicantLegalStructureValue === "kgaa";
  const isParagraph6EegActive = watch("isParagraph_6EegActive");
  const projectHasSitesWithInvoicing = sites.some(
    (site) => site.invoicing_active
  );
  const isP6ActiveOrProjectHasSitesWithInvoicing =
    isParagraph6EegActive ||
    (projectHasSitesWithInvoicing && !!isSolarContractor);

  generalFieldsVisibility.showGeneralPartnerFields =
    isEditMode &&
    isLegalStructureWithPhg &&
    !!isP6ActiveOrProjectHasSitesWithInvoicing;

  // Side effect: clear general partner fields when conditions change
  useEffect(() => {
    if (
      isEditMode &&
      (!isLegalStructureWithPhg || !isP6ActiveOrProjectHasSitesWithInvoicing)
    ) {
      setValue("generalPartnerName", "");
      setValue("generalPartnerCity", "");
      setValue("generalPartnerCourt", "");
      setValue("generalPartnerRegisterNumber", "");
      setValue("generalPartnerCeo", "");
    }
  }, [
    isEditMode,
    isLegalStructureWithPhg,
    isP6ActiveOrProjectHasSitesWithInvoicing,
    setValue
  ]);

  // Side effect: clear redispatch import when erloesmonitoring is disabled
  useEffect(() => {
    if (isEditMode && !isErloesmonitoringActive) {
      setValue("isRedispatchImportActive", false);
    }
  }, [isEditMode, isErloesmonitoringActive, setValue]);

  // Regulatory duties tab
  const showEigenerzeugungPlusFields =
    !siteCategories || siteCategories.is_eigenerzeugung_plus;
  const showSmallMediumEnterpriseField =
    watch("smallMediumEnterprise") !== null;
  const isManufacturingCompany =
    watch("manufacturingIndustry") !== YesNoChoice.no;
  const hasRegulatoryFieldsToShow =
    showEigenerzeugungPlusFields || showSmallMediumEnterpriseField;
  const showRegulatoryDutiesTab =
    !!watch("shouldRegulatoryDutiesBeChecked") && hasRegulatoryFieldsToShow;

  // Tax form tab
  const taxFormsSubmissionInOwnName = watch("taxFormsSubmissionInOwnName");

  // Delivery tab
  const invoiceSuppliedAddressIsGeneralAddress = watch(
    "invoiceSuppliedAddressIsGeneralAddress"
  );
  const invoiceSupplierIsGbr = watch("invoiceSupplierIsGbr");

  return {
    generalFieldsVisibility,

    // Regulatory duties tab
    showRegulatoryDutiesTab,
    showEigenerzeugungPlusFields,
    showSmallMediumEnterpriseField,
    isManufacturingCompany,

    // Tax form tab
    taxFormsSubmissionInOwnName,

    // Delivery tab
    isParagraph6EegActive: !!isParagraph6EegActive,
    isSolarContractor,
    projectHasSitesWithInvoicing,
    invoiceSuppliedAddressIsGeneralAddress,
    invoiceSupplierIsGbr
  };
}
